import React from 'react';
import { Crown, Check, Sparkles, Shield, Zap, Gift } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useSubscriptionManagement } from '@/hooks/useSubscriptionManagement';

interface PremiumBenefitsDisplayProps {
  userRole?: string;
}

export const PremiumBenefitsDisplay: React.FC<PremiumBenefitsDisplayProps> = ({
  userRole
}) => {
  const { openCustomerPortal, isLoading } = useSubscriptionManagement();

  // Features baseado no role
  const premiumFeatures = userRole === 'sponsor' ? [
    { icon: <Zap className="h-5 w-5" />, name: 'Mensagens ilimitadas', description: 'Converse sem limites' },
    { icon: <Sparkles className="h-5 w-5" />, name: 'Likes sem limite', description: 'Demonstre interesse livremente' },
    { icon: <Shield className="h-5 w-5" />, name: 'Ver visitantes do perfil', description: 'Saiba quem visitou você' },
    { icon: <Crown className="h-5 w-5" />, name: 'Filtros avançados de busca', description: 'Encontre conexões específicas' },
    { icon: <Sparkles className="h-5 w-5" />, name: 'Destaque no feed', description: 'Apareça em primeiro lugar' },
    { icon: <Shield className="h-5 w-5" />, name: 'Selo de verificação premium', description: 'Credibilidade máxima' },
    { icon: <Zap className="h-5 w-5" />, name: 'Acesso prioritário a perfis', description: 'Veja os melhores primeiro' },
    { icon: <Gift className="h-5 w-5" />, name: 'Match garantido mensal', description: 'Pelo menos 1 match por mês' },
    { icon: <Crown className="h-5 w-5" />, name: 'Suporte prioritário 24/7', description: 'Atendimento VIP exclusivo' },
    { icon: <Shield className="h-5 w-5" />, name: 'Modo invisível', description: 'Navegue sem ser visto' }
  ] : [
    { icon: <Zap className="h-5 w-5" />, name: 'Mensagens ilimitadas', description: 'Converse sem limites' },
    { icon: <Sparkles className="h-5 w-5" />, name: 'Likes sem limite', description: 'Demonstre interesse livremente' },
    { icon: <Shield className="h-5 w-5" />, name: 'Ver visitantes do perfil', description: 'Saiba quem visitou você' },
    { icon: <Sparkles className="h-5 w-5" />, name: 'Destaque no feed', description: 'Apareça em primeiro lugar' },
    { icon: <Shield className="h-5 w-5" />, name: 'Selo de verificação premium', description: 'Credibilidade máxima' },
    { icon: <Zap className="h-5 w-5" />, name: 'Acesso prioritário a perfis', description: 'Veja os melhores primeiro' },
    { icon: <Gift className="h-5 w-5" />, name: 'Match garantido mensal', description: 'Pelo menos 1 match por mês' },
    { icon: <Crown className="h-5 w-5" />, name: 'Suporte prioritário 24/7', description: 'Atendimento VIP exclusivo' },
    { icon: <Shield className="h-5 w-5" />, name: 'Modo invisível', description: 'Navegue sem ser visto' }
  ];

  return (
    <div className="max-w-4xl mx-auto">
      {/* Header celebrativo */}
      <div className="text-center mb-8">
        <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-luxury-gold to-luxury-red rounded-full mb-4">
          <Crown className="h-8 w-8 text-white" />
        </div>
        
        <Badge className="bg-gradient-to-r from-luxury-gold to-luxury-red text-white mb-4 px-4 py-2">
          <Sparkles className="w-4 h-4 mr-2" />
          Você tem o melhor plano!
        </Badge>
        
        <h2 className="text-3xl font-bold text-white mb-2">
          Plano Premium Ativo
        </h2>
        <p className="text-gray-300 text-lg">
          Aproveite todos esses benefícios exclusivos
        </p>
      </div>

      {/* Grid de benefícios */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
        {premiumFeatures.map((feature, index) => (
          <div
            key={index}
            className="bg-white/10 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/15 transition-all duration-300"
          >
            <div className="flex items-start space-x-4">
              <div className="flex-shrink-0 p-2 bg-gradient-to-r from-luxury-gold/20 to-luxury-red/20 rounded-lg">
                <div className="text-luxury-gold">
                  {feature.icon}
                </div>
              </div>
              <div className="flex-1">
                <div className="flex items-center space-x-2 mb-1">
                  <h3 className="font-semibold text-white">{feature.name}</h3>
                  <Check className="h-4 w-4 text-luxury-gold" />
                </div>
                <p className="text-sm text-gray-400">{feature.description}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Dicas de uso */}
      <div className="bg-gradient-to-r from-luxury-gold/10 to-luxury-red/10 border border-luxury-gold/20 rounded-xl p-6 mb-8">
        <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
          <Sparkles className="h-5 w-5 mr-2 text-luxury-gold" />
          Dicas para aproveitar seu plano Premium
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-gray-300">
          <div className="flex items-start space-x-2">
            <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0"></div>
            <span>Use os filtros avançados para encontrar conexões mais compatíveis</span>
          </div>
          <div className="flex items-start space-x-2">
            <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0"></div>
            <span>Ative o modo invisível quando quiser navegar discretamente</span>
          </div>
          <div className="flex items-start space-x-2">
            <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0"></div>
            <span>Aproveite as mensagens ilimitadas para criar conexões profundas</span>
          </div>
          <div className="flex items-start space-x-2">
            <div className="w-2 h-2 bg-luxury-gold rounded-full mt-2 flex-shrink-0"></div>
            <span>Seu selo de verificação premium aumenta sua credibilidade</span>
          </div>
        </div>
      </div>

      {/* Botão de gerenciar assinatura */}
      <div className="text-center">
        <Button
          onClick={openCustomerPortal}
          disabled={isLoading}
          className="bg-gradient-to-r from-luxury-gold to-luxury-red hover:from-luxury-gold/80 hover:to-luxury-red/80 text-white px-8 py-3 text-lg font-semibold"
        >
          {isLoading ? (
            <>
              <Crown className="h-5 w-5 mr-2 animate-spin" />
              Carregando...
            </>
          ) : (
            <>
              <Crown className="h-5 w-5 mr-2" />
              Gerenciar Assinatura
            </>
          )}
        </Button>
        <p className="text-sm text-gray-400 mt-2">
          Altere seus dados de pagamento, veja histórico e muito mais
        </p>
      </div>
    </div>
  );
};